import { javaServerApiUrl } from "@repo/config";
import type { User } from "firebase/auth";
import {
  type DocumentData,
  type DocumentReference,
  collection,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";

import { createApiClient, createCloudFunctionClient } from "../apiClient.js";
import { db } from "../firebase.js";

const javaServerApiClient = createApiClient({
  baseURL: javaServerApiUrl,
  teamType: "clinic",
});

const expressServerApiClient = createCloudFunctionClient("api", "clinic");

export type ClinicClientCompatibleAuthState = {
  user: User | null;
  authenticated: boolean;
  isAdmin: boolean;
  loadingAuthState: boolean;
  currentTeamId?: string;
  currentTeamName?: string;
  currentTeamType?: "admin" | "clinic" | "lab";
  isTeamOwner: boolean;
  rdsCompanyId: number | null;
  rdsAccountId: number | null;
};

export const createSignedOutClinicClientAuthState =
  (): ClinicClientCompatibleAuthState => ({
    user: null,
    authenticated: false,
    isAdmin: false,
    loadingAuthState: false,
    currentTeamId: undefined,
    currentTeamName: undefined,
    currentTeamType: undefined,
    isTeamOwner: false,
    rdsCompanyId: null,
    rdsAccountId: null,
  });

const syncClinicClaims = async (user: User) => {
  try {
    const response = await expressServerApiClient.post(
      "/clinic/auth/verifyAndGrantClinicPrivileges",
    );

    if (response.data?.claimsUpdated) {
      await user.getIdToken(true);
    }
  } catch (error) {
    console.error("클리닉 custom claims 동기화 중 오류 발생:", error);
  }
};

const checkTeamMembership = async (
  userId: string,
): Promise<DocumentReference<DocumentData> | null> => {
  try {
    const teamsCollection = collection(db, "teams");
    const teamsSnapshot = await getDocs(teamsCollection);

    for (const teamDoc of teamsSnapshot.docs) {
      const membershipRef = doc(teamDoc.ref, "memberships", userId);
      const membershipDoc = await getDoc(membershipRef);

      if (membershipDoc.exists()) {
        return teamDoc.ref;
      }
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getUserRdsData = async (
  teamDoc: DocumentReference<DocumentData> | null,
) => {
  if (!teamDoc) {
    return null;
  }

  return (
    (await javaServerApiClient.get("/api/v1/accounts")).data?.result ?? null
  );
};

export const resolveClinicClientCompatibleAuthState = async (
  user: User,
  { syncClaims = false }: { syncClaims?: boolean } = {},
): Promise<ClinicClientCompatibleAuthState> => {
  if (syncClaims) {
    await syncClinicClaims(user);
  }

  const teamDoc = await checkTeamMembership(user.uid);
  const userRdsData = await getUserRdsData(teamDoc);
  const tokenResponse = await user.getIdTokenResult();
  const teamSnapshot = teamDoc ? await getDoc(teamDoc) : null;
  const membershipSnapshot = teamDoc
    ? await getDoc(doc(db, "teams", teamDoc.id, "memberships", user.uid))
    : null;

  return {
    user,
    authenticated: true,
    isAdmin: Boolean(tokenResponse.claims?.admin),
    loadingAuthState: false,
    currentTeamId: teamDoc?.id,
    currentTeamName: teamSnapshot?.data()?.name,
    currentTeamType: teamSnapshot?.data()?.type,
    isTeamOwner: membershipSnapshot?.data()?.role === "owner",
    rdsCompanyId: userRdsData?.company?.id ?? userRdsData?.companyId ?? null,
    rdsAccountId: userRdsData?.id ?? null,
  };
};
